// ============================================================
// src/shack/encuadrador.js — Editor de cuadro interactivo
// ============================================================
// Estado del certificado dentro del marco:
//   pos  {x, y}           — posición en px relativos al canvas
//   size {w, h}           — tamaño en px en el canvas
//   crop {t, r, b, l}     — fracción a ocultar por cada lado (0..1), no destructivo
//
// Handles:
//   Esquinas (4): escalan el cert manteniendo o no el aspecto
//   Lados (4):    recortan el lado correspondiente
//   Interior:     mueve el cert (cursor manito)
// ============================================================

import { generarThumbnail, generarThumbnailManagedDrive } from './thumbnails.js';
import { pdfBlobToImageBitmap }                          from './pdfUtils.js';
import { t } from '../i18n.js';
import { FRAME_GEOMETRY } from './shackConfig.js';

// ── Constantes ────────────────────────────────────────────────
const FRAME_PNG_URLS = {
  black:  '/assets/frames/frame_black.png',
  madera: '/assets/frames/frame_madera.png',
};
const CANVAS_W    = 560;
const CANVAS_H    = 400;
const HANDLE_R    = 7;   // radio de los handles en px
const WALL_COLOR  = '#c8b8a8';

const MODELOS  = [
  { key: 'black',  i18nKey: 'encuadrador.modelo.negro'  },
  { key: 'madera', i18nKey: 'encuadrador.modelo.madera' },
];
const ASPECTOS = [
  { i18nKey: 'encuadrador.aspecto.libre', ratio: null    },
  { label: '4:3',   ratio: 4/3     },
  { label: '3:2',   ratio: 3/2     },
  { label: 'A4',    ratio: 297/210 },
  { label: '16:9',  ratio: 16/9    },
];

// ── Estado ────────────────────────────────────────────────────
let _ctx       = null;   // { item, onGuardar }
let _urlImg    = null;
let _imgBitmap = null;   // ImageBitmap del certificado

// Estado editable del cert (en coordenadas del canvas)
let _cert = {
  x: 0, y: 0,       // posición top-left en canvas
  w: 0, h: 0,       // tamaño en canvas
  crop: { t: 0, r: 0, b: 0, l: 0 }, // fracción oculta por lado (0..1)
};

// Drag state
let _drag = null;
let _marcoCache = {};
// { type: 'move'|'scale-tl'|'scale-tr'|'scale-bl'|'scale-br'
//         |'crop-t'|'crop-r'|'crop-b'|'crop-l'
//   startX, startY, origCert }

// ─────────────────────────────────────────────────────────────
// API PÚBLICA
// ─────────────────────────────────────────────────────────────

export async function abrirEncuadrador(item, urlImg, onGuardar) {

  _ctx     = { item: { ...item }, onGuardar };
  _urlImg  = null;
  _imgBitmap = null;
  _drag    = null;

  _asegurarModal();
  _poblarModal();
  _mostrarModal();

  _renderPlaceholder();

  _urlImg = await _obtenerUrl(urlImg, item.fileId);

  if (_urlImg) {
    try {
      const resp = await fetch(_urlImg);
      const blob = await resp.blob();
      if (blob.type === 'application/pdf') {
        _imgBitmap = await pdfBlobToImageBitmap(blob);
      } else {
        _imgBitmap = await createImageBitmap(blob);
      }
    } catch(e) {
      console.warn('[ENC] fetch/createImageBitmap falló:', e.message, '— usando Image fallback');
      _imgBitmap = await _cargarImageBitmap(_urlImg);
    }
  }

  _calcularCanvas();

  if (item.frameRatioManual && item.certState) {
    _cert = JSON.parse(JSON.stringify(item.certState));
    _cert.x = item.certState.xRel * _canvasW();
    _cert.y = item.certState.yRel * _canvasH();
    _cert.w = item.certState.wRel * _canvasW();
    _cert.h = item.certState.hRel * _canvasH();
  } else {
    _resetCert();
  }

  _render();
}

export function cerrarEncuadrador() {
  document.getElementById('modal-encuadrador')
    ?.classList.remove('modal--visible');
  _imgBitmap?.close();
  _ctx = null; _urlImg = null; _imgBitmap = null; _drag = null;
}

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

function _canvasW() {
  return document.getElementById('encuadrador-canvas')?.width || CANVAS_W;
}
function _canvasH() {
  return document.getElementById('encuadrador-canvas')?.height || CANVAS_H;
}

function _calcularCanvas() {
  const canvas = document.getElementById('encuadrador-canvas');
  if (!canvas || !_ctx) return;
  const { certRect } = FRAME_GEOMETRY;

  let ratio;
  if (_ctx.item.frameRatioManual && _ctx.item.frameRatio) {
    ratio = _ctx.item.frameRatio;
  } else {
    const bitmapRatio = (_imgBitmap?.width && _imgBitmap?.height)
      ? _imgBitmap.width / _imgBitmap.height
      : (_ctx.item.imgRatio ?? null);
    ratio = bitmapRatio
      ? bitmapRatio * (certRect.h / certRect.w)
      : 1.5;
  }

  let cw = CANVAS_W, ch = cw / ratio;
  if (ch > CANVAS_H) { ch = CANVAS_H; cw = ch * ratio; }
  canvas.width  = Math.round(cw);
  canvas.height = Math.round(ch);
}

// Calcula el rect del interior del marco en coordenadas canvas
function _innerRect() {
  const cw = _canvasW(), ch = _canvasH();
  const { certRect } = FRAME_GEOMETRY;
  return {
    x: certRect.x * cw,
    y: certRect.y * ch,
    w: certRect.w * cw,
    h: certRect.h * ch,
  };
}

// Resetear cert al interior del marco — llena certRect, letterboxed si el aspecto no coincide
function _resetCert() {
  const ir = _innerRect();
  const bm = _imgBitmap;
  if (!bm) {
    _cert = { x: ir.x, y: ir.y, w: ir.w, h: ir.h, crop: { t: 0, r: 0, b: 0, l: 0 } };
    return;
  }
  const ar = bm.width / bm.height;
  let w = ir.w, h = w / ar;
  if (h > ir.h) { h = ir.h; w = h * ar; }
  _cert = {
    x: ir.x + (ir.w - w) / 2,
    y: ir.y + (ir.h - h) / 2,
    w, h,
    crop: { t: 0, r: 0, b: 0, l: 0 },
  };
}

async function _cargarImageBitmap(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = async () => {
      try { resolve(await createImageBitmap(img)); }
      catch(e) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function _obtenerUrl(urlImg, fileId) {
  if (!urlImg && !fileId) return null;
  if (urlImg) return urlImg;
  if (fileId) {
    const isManagedDrive = _ctx?.item?.storageType === 'managed_drive';
    const fn = isManagedDrive ? generarThumbnailManagedDrive : generarThumbnail;
    try { const u = await fn(fileId); if (u) return u; } catch(e) {}
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// RENDER
// ─────────────────────────────────────────────────────────────

function _renderPlaceholder() {
  const canvas = document.getElementById('encuadrador-canvas');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const { width: cw, height: ch } = canvas;
  ctx.fillStyle = WALL_COLOR;
  ctx.fillRect(0, 0, cw, ch);
  const ir = _innerRect();
  ctx.fillStyle = '#f0ebe0';
  ctx.fillRect(ir.x, ir.y, ir.w, ir.h);
  ctx.fillStyle = '#b0a090';
  ctx.font = `${Math.round(ir.w * 0.05)}px system-ui`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(t('encuadrador.cargando'), ir.x + ir.w/2, ir.y + ir.h/2);
}

function _render() {
  const canvas = document.getElementById('encuadrador-canvas');
  if (!canvas || !_ctx) return;
  const ctx = canvas.getContext('2d');
  const cw = canvas.width, ch = canvas.height;

  ctx.clearRect(0, 0, cw, ch);

  // 1. Fondo pared
  ctx.fillStyle = WALL_COLOR;
  ctx.fillRect(0, 0, cw, ch);

  // 2. Certificado DETRÁS del marco
  if (_imgBitmap) {
    const c = _cert;
    const bw = _imgBitmap.width, bh = _imgBitmap.height;
    const srcX = c.crop.l * bw;
    const srcY = c.crop.t * bh;
    const srcW = (1 - c.crop.l - c.crop.r) * bw;
    const srcH = (1 - c.crop.t - c.crop.b) * bh;
    const dstX = c.x + c.w * c.crop.l;
    const dstY = c.y + c.h * c.crop.t;
    const dstW = c.w * (1 - c.crop.l - c.crop.r);
    const dstH = c.h * (1 - c.crop.t - c.crop.b);
    ctx.drawImage(_imgBitmap, srcX, srcY, srcW, srcH, dstX, dstY, dstW, dstH);
  }

  // 3. Marco PNG con clearRect en certRect — el cert se ve a través del hueco
  _dibujarMarco(ctx, cw, ch, _ctx.item.frameModel ?? 'black');

  // 4. Handles
  _dibujarHandles(ctx);
}

function _dibujarMarco(ctx, cw, ch, frameKey) {
  const cached = _marcoCache[frameKey];
  if (cached === 'error') return;
  if (cached) { ctx.drawImage(cached, 0, 0, cw, ch); return; }

  const url = FRAME_PNG_URLS[frameKey] ?? FRAME_PNG_URLS.black;

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const off = document.createElement('canvas');
    off.width  = img.naturalWidth;
    off.height = img.naturalHeight;
    const octx = off.getContext('2d');
    octx.drawImage(img, 0, 0);

    const { certRect } = FRAME_GEOMETRY;
    octx.clearRect(
      Math.floor(off.width  * certRect.x),
      Math.floor(off.height * certRect.y),
      Math.ceil(off.width   * certRect.w),
      Math.ceil(off.height  * certRect.h)
    );

    _marcoCache[frameKey] = off;
    _render();
  };
  img.onerror = () => {
    console.error('[ENC] error cargando marco:', url);
    _marcoCache[frameKey] = 'error';
  };
  img.src = url;
}

// Verdadero cuando no hay proporción fija seleccionada
function _esModoLibre() {
  return !_ctx?.item.frameRatio || _ctx.item.frameRatio === 'libre';
}

// Handles de las esquinas del CUADRO (solo en modo libre)
function _frameHandles() {
  const cw = _canvasW(), ch = _canvasH();
  return [
    { id: 'frame-tl', x: 0,  y: 0  },
    { id: 'frame-tr', x: cw, y: 0  },
    { id: 'frame-bl', x: 0,  y: ch },
    { id: 'frame-br', x: cw, y: ch },
  ];
}

function _dibujarHandles(ctx) {
  const c = _cert;
  const vx = c.x + c.w * c.crop.l;
  const vy = c.y + c.h * c.crop.t;
  const vw = c.w * (1 - c.crop.l - c.crop.r);
  const vh = c.h * (1 - c.crop.t - c.crop.b);

  // Borde del cert visible (línea punteada)
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 3]);
  ctx.strokeRect(vx, vy, vw, vh);
  ctx.setLineDash([]);
  ctx.restore();

  // Handles de escala del cert (azul, esquinas del rect total)
  const corners = [
    { id: 'scale-tl', x: c.x,       y: c.y       },
    { id: 'scale-tr', x: c.x + c.w, y: c.y       },
    { id: 'scale-bl', x: c.x,       y: c.y + c.h },
    { id: 'scale-br', x: c.x + c.w, y: c.y + c.h },
  ];
  corners.forEach(h => _drawHandle(ctx, h.x, h.y, '#ffffff', '#3b6bff'));

  // Handles de crop del cert (naranja, bordes del área visible)
  const crops = [
    { id: 'crop-t', x: vx + vw/2, y: vy        },
    { id: 'crop-b', x: vx + vw/2, y: vy + vh   },
    { id: 'crop-l', x: vx,        y: vy + vh/2 },
    { id: 'crop-r', x: vx + vw,   y: vy + vh/2 },
  ];
  crops.forEach(h => _drawHandle(ctx, h.x, h.y, '#ffffff', '#ff9020'));

  // Handles del CUADRO (verde, esquinas del canvas) — solo en modo Libre
  if (_esModoLibre()) {
    const cw = _canvasW(), ch = _canvasH();
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(1, 1, cw - 2, ch - 2);
    ctx.setLineDash([]);
    ctx.restore();

    _frameHandles().forEach(h => _drawHandle(ctx, h.x, h.y, '#ffffff', '#22dd88'));
  }
}

function _drawHandle(ctx, x, y, fill, stroke) {
  ctx.beginPath();
  ctx.arc(x, y, HANDLE_R, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 2.5;
  ctx.stroke();
}

// ─────────────────────────────────────────────────────────────
// INTERACCIÓN (mouse / touch)
// ─────────────────────────────────────────────────────────────

function _hitHandle(mx, my) {
  const c = _cert;
  const vx = c.x + c.w * c.crop.l;
  const vy = c.y + c.h * c.crop.t;
  const vw = c.w * (1 - c.crop.l - c.crop.r);
  const vh = c.h * (1 - c.crop.t - c.crop.b);

  // Handles del CUADRO (prioridad en modo libre — están en esquinas del canvas)
  if (_esModoLibre()) {
    for (const h of _frameHandles()) {
      const dx = mx - h.x, dy = my - h.y;
      if (dx*dx + dy*dy <= (HANDLE_R + 6) ** 2) return h.id;
    }
  }

  const handles = [
    { id: 'scale-tl', x: c.x,       y: c.y        },
    { id: 'scale-tr', x: c.x + c.w, y: c.y        },
    { id: 'scale-bl', x: c.x,       y: c.y + c.h  },
    { id: 'scale-br', x: c.x + c.w, y: c.y + c.h  },
    { id: 'crop-t',   x: vx+vw/2,   y: vy          },
    { id: 'crop-b',   x: vx+vw/2,   y: vy+vh       },
    { id: 'crop-l',   x: vx,        y: vy+vh/2     },
    { id: 'crop-r',   x: vx+vw,     y: vy+vh/2     },
  ];
  for (const h of handles) {
    const dx = mx - h.x, dy = my - h.y;
    if (dx*dx + dy*dy <= (HANDLE_R + 4) ** 2) return h.id;
  }
  if (mx >= vx && mx <= vx+vw && my >= vy && my <= vy+vh) return 'move';
  return null;
}

function _canvasPos(e) {
  const canvas = document.getElementById('encuadrador-canvas');
  const rect   = canvas.getBoundingClientRect();
  const scaleX = canvas.width  / rect.width;
  const scaleY = canvas.height / rect.height;
  const clientX = e.touches ? e.touches[0].clientX : e.clientX;
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top)  * scaleY,
  };
}

function _onPointerDown(e) {
  const { x, y } = _canvasPos(e);
  const hit = _hitHandle(x, y);
  if (!hit) return;
  e.preventDefault();
  const clientX = e.touches ? e.touches[0].clientX : e.clientX;
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;
  const bm = _imgBitmap;
  _drag = {
    type: hit,
    startX: x, startY: y,
    startClientX: clientX, startClientY: clientY,
    origCert: JSON.parse(JSON.stringify(_cert)),
    origW: _canvasW(),
    origH: _canvasH(),
    shiftLocked: !!e.shiftKey,
    imgRatio: (bm && bm.width && bm.height) ? (bm.width / bm.height) : null,
  };
  _actualizarCursor(hit);
}

// Redimensiona el canvas del cuadro SIN tocar el cert
function _aplicarNuevoTamanoCuadro(newW, newH) {
  const canvas = document.getElementById('encuadrador-canvas');
  if (!canvas) return;

  canvas.width  = Math.round(Math.max(200, newW));
  canvas.height = Math.round(Math.max(120, newH));

  if (_ctx) _ctx.item.frameRatio = canvas.width / canvas.height;
}

function _onPointerMove(e) {
  if (!_drag) {
    const { x, y } = _canvasPos(e);
    const hit = _hitHandle(x, y);
    _actualizarCursor(hit);
    return;
  }
  e.preventDefault();

  // Para frame-* usar coordenadas de PANTALLA para evitar
  // que el cambio de tamaño del canvas distorsione el delta
  const isFrameDrag = _drag.type.startsWith('frame-');

  let dx, dy;
  if (isFrameDrag) {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dx = clientX - _drag.startClientX;
    dy = clientY - _drag.startClientY;
  } else {
    const { x, y } = _canvasPos(e);
    dx = x - _drag.startX;
    dy = y - _drag.startY;
  }

  const o = _drag.origCert;

  switch (_drag.type) {
    case 'move':
      _cert.x = o.x + dx;
      _cert.y = o.y + dy;
      break;

    case 'scale-br': {
      const newW = Math.max(30, o.w + dx);
      const newH = Math.max(20, o.h + dy);
      if (_drag.shiftLocked && _drag.imgRatio) {
        const byW = newW / o.w;
        const byH = newH / o.h;
        const factor = Math.max(byW, byH);
        _cert.w = Math.max(30, o.w * factor);
        _cert.h = _cert.w / _drag.imgRatio;
      } else {
        _cert.w = newW;
        _cert.h = newH;
      }
      break;
    }
    case 'scale-tl': {
      const newW = Math.max(30, o.w - dx);
      const newH = Math.max(20, o.h - dy);
      if (_drag.shiftLocked && _drag.imgRatio) {
        const byW = newW / o.w;
        const byH = newH / o.h;
        const factor = Math.max(byW, byH);
        _cert.w = Math.max(30, o.w * factor);
        _cert.h = _cert.w / _drag.imgRatio;
      } else {
        _cert.w = newW;
        _cert.h = newH;
      }
      _cert.x = o.x + o.w - _cert.w;
      _cert.y = o.y + o.h - _cert.h;
      break;
    }
    case 'scale-tr': {
      const newW = Math.max(30, o.w + dx);
      const newH = Math.max(20, o.h - dy);
      if (_drag.shiftLocked && _drag.imgRatio) {
        const byW = newW / o.w;
        const byH = newH / o.h;
        const factor = Math.max(byW, byH);
        _cert.w = Math.max(30, o.w * factor);
        _cert.h = _cert.w / _drag.imgRatio;
      } else {
        _cert.w = newW;
        _cert.h = newH;
      }
      _cert.y = o.y + o.h - _cert.h;
      break;
    }
    case 'scale-bl': {
      const newW = Math.max(30, o.w - dx);
      const newH = Math.max(20, o.h + dy);
      if (_drag.shiftLocked && _drag.imgRatio) {
        const byW = newW / o.w;
        const byH = newH / o.h;
        const factor = Math.max(byW, byH);
        _cert.w = Math.max(30, o.w * factor);
        _cert.h = _cert.w / _drag.imgRatio;
      } else {
        _cert.w = newW;
        _cert.h = newH;
      }
      _cert.x = o.x + o.w - _cert.w;
      break;
    }

    // ── Resize del CUADRO (modo libre) ─────────────────────────────────────
    case 'frame-br': {
      const newW = Math.max(200, _drag.origW + dx);
      const newH = Math.max(120, _drag.origH + dy);
      _aplicarNuevoTamanoCuadro(newW, newH);
      break;
    }
    case 'frame-tl': {
      const newW = Math.max(200, _drag.origW - dx);
      const newH = Math.max(120, _drag.origH - dy);
      _aplicarNuevoTamanoCuadro(newW, newH);
      break;
    }
    case 'frame-tr': {
      const newW = Math.max(200, _drag.origW + dx);
      const newH = Math.max(120, _drag.origH - dy);
      _aplicarNuevoTamanoCuadro(newW, newH);
      break;
    }
    case 'frame-bl': {
      const newW = Math.max(200, _drag.origW - dx);
      const newH = Math.max(120, _drag.origH + dy);
      _aplicarNuevoTamanoCuadro(newW, newH);
      break;
    }

    case 'crop-t': {
      const delta = Math.max(-o.crop.t, Math.min(1 - o.crop.t - o.crop.b - 0.05, dy / o.h));
      _cert.crop.t = Math.max(0, o.crop.t + delta);
      break;
    }
    case 'crop-b': {
      const delta = Math.max(-o.crop.b, Math.min(1 - o.crop.t - o.crop.b - 0.05, -dy / o.h));
      _cert.crop.b = Math.max(0, o.crop.b + delta);
      break;
    }
    case 'crop-l': {
      const delta = Math.max(-o.crop.l, Math.min(1 - o.crop.l - o.crop.r - 0.05, dx / o.w));
      _cert.crop.l = Math.max(0, o.crop.l + delta);
      break;
    }
    case 'crop-r': {
      const delta = Math.max(-o.crop.r, Math.min(1 - o.crop.l - o.crop.r - 0.05, -dx / o.w));
      _cert.crop.r = Math.max(0, o.crop.r + delta);
      break;
    }
  }
  _render();
}

function _onPointerUp() {
  _drag = null;
  const canvas = document.getElementById('encuadrador-canvas');
  if (canvas) canvas.style.cursor = 'default';
}

function _actualizarCursor(hit) {
  const canvas = document.getElementById('encuadrador-canvas');
  if (!canvas) return;
  const cursors = {
    'move':      'grab',
    'scale-tl':  'nwse-resize',
    'scale-br':  'nwse-resize',
    'scale-tr':  'nesw-resize',
    'scale-bl':  'nesw-resize',
    'crop-t':    'ns-resize',
    'crop-b':    'ns-resize',
    'crop-l':    'ew-resize',
    'crop-r':    'ew-resize',
    'frame-tl':  'nwse-resize',
    'frame-br':  'nwse-resize',
    'frame-tr':  'nesw-resize',
    'frame-bl':  'nesw-resize',
  };
  canvas.style.cursor = hit ? (cursors[hit] || 'default') : 'default';
}

// ─────────────────────────────────────────────────────────────
// MODAL HTML
// ─────────────────────────────────────────────────────────────

function _asegurarModal() {
  if (document.getElementById('modal-encuadrador')) return;

  const modelosHTML = MODELOS.map(m =>
    `<button class="enc-opcion" data-marco="${m.key}" data-i18n="${m.i18nKey}">${t(m.i18nKey)}</button>`
  ).join('');

  const aspectosHTML = ASPECTOS.map((a, i) => {
    const text = a.i18nKey ? t(a.i18nKey) : a.label;
    const i18nAttr = a.i18nKey ? ` data-i18n="${a.i18nKey}"` : '';
    return `<button class="enc-opcion" data-aspecto="${i}"${i18nAttr}>${text}</button>`;
  }).join('');

  document.body.insertAdjacentHTML('beforeend', `
    <div id="modal-encuadrador" class="modal-overlay encuadrador-overlay" role="dialog" aria-modal="true">
      <div class="encuadrador-panel">

        <div class="encuadrador-panel__header">
          <h2 class="encuadrador-panel__titulo" data-i18n="encuadrador.titulo">${t('encuadrador.titulo')}</h2>
          <button id="btn-cerrar-encuadrador" class="modal-panel__cerrar" aria-label="Cerrar">✕</button>
        </div>

        <div class="encuadrador-panel__body">

          <!-- Barra de herramientas de alineación -->
          <div class="enc-toolbar">
            <span class="enc-toolbar__label" data-i18n="encuadrador.toolbar.alinear">${t('encuadrador.toolbar.alinear')}</span>
            <button class="enc-tool-btn" data-align="h"   data-i18n-title="encuadrador.toolbar.centrar-h"  title="${t('encuadrador.toolbar.centrar-h')}">⟷</button>
            <button class="enc-tool-btn" data-align="v"   data-i18n-title="encuadrador.toolbar.centrar-v"  title="${t('encuadrador.toolbar.centrar-v')}">⟵↕</button>
            <button class="enc-tool-btn" data-align="hv"  data-i18n-title="encuadrador.toolbar.centrar-hv" title="${t('encuadrador.toolbar.centrar-hv')}">⊕</button>
            <button class="enc-tool-btn" data-align="fit" data-i18n-title="encuadrador.toolbar.ajustar"    title="${t('encuadrador.toolbar.ajustar')}">⤢</button>
            <button class="enc-tool-btn" data-align="reset-crop" data-i18n-title="encuadrador.toolbar.quitar-crop" title="${t('encuadrador.toolbar.quitar-crop')}">✂️</button>
          </div>

          <!-- Canvas interactivo -->
          <div class="encuadrador-preview">
            <canvas id="encuadrador-canvas" width="${CANVAS_W}" height="${CANVAS_H}"></canvas>
          </div>

          <!-- Controles de cuadro -->
          <div class="encuadrador-controles">
            <div class="encuadrador-seccion">
              <label class="encuadrador-label" data-i18n="encuadrador.marco.label">${t('encuadrador.marco.label')}</label>
              <div class="encuadrador-opciones" id="enc-marcos">
                ${modelosHTML}
              </div>
            </div>
            <div class="encuadrador-seccion">
              <label class="encuadrador-label" data-i18n="encuadrador.proporcion.label">${t('encuadrador.proporcion.label')}</label>
              <div class="encuadrador-opciones" id="enc-aspectos">
                ${aspectosHTML}
              </div>
            </div>
          </div>

        </div>

        <div class="encuadrador-panel__footer">
          <button id="btn-enc-cancelar" class="btn-secundario" data-i18n="encuadrador.btn.cancelar">${t('encuadrador.btn.cancelar')}</button>
          <button id="btn-enc-guardar"  class="btn-primario"   data-i18n="encuadrador.btn.guardar">${t('encuadrador.btn.guardar')}</button>
        </div>

      </div>
    </div>
  `);

  // Eventos canvas
  const canvas = document.getElementById('encuadrador-canvas');
  canvas.addEventListener('mousedown',  _onPointerDown);
  canvas.addEventListener('mousemove',  _onPointerMove);
  canvas.addEventListener('mouseup',    _onPointerUp);
  canvas.addEventListener('mouseleave', _onPointerUp);
  canvas.addEventListener('touchstart', _onPointerDown, { passive: false });
  canvas.addEventListener('touchmove',  _onPointerMove,  { passive: false });
  canvas.addEventListener('touchend',   _onPointerUp);

  // Eventos botones
  document.getElementById('btn-cerrar-encuadrador').addEventListener('click', cerrarEncuadrador);
  document.getElementById('btn-enc-cancelar').addEventListener('click', cerrarEncuadrador);
  document.getElementById('btn-enc-guardar').addEventListener('click', _guardar);

  document.getElementById('enc-marcos').addEventListener('click', e => {
    const btn = e.target.closest('[data-marco]');
    if (!btn || !_ctx) return;
    _ctx.item.frameModel = btn.dataset.marco;
    _marcarActivo('enc-marcos', 'data-marco', btn.dataset.marco);
    _render();
  });

  document.getElementById('enc-aspectos').addEventListener('click', e => {
    const btn = e.target.closest('[data-aspecto]');
    if (!btn || !_ctx) return;
    const idx = parseInt(btn.dataset.aspecto);
    const asp = ASPECTOS[idx];
    if (asp.ratio) {
      _ctx.item.frameRatio = asp.ratio;
      _ctx.item.frameRatioManual = true;
    } else {
      _ctx.item.frameRatio = null;
      _ctx.item.frameRatioManual = false;
    }
    _calcularCanvas();
    _resetCert();
    _marcarActivo('enc-aspectos', 'data-aspecto', btn.dataset.aspecto);
    _render();
  });

  // Herramientas de alineación
  document.querySelector('.enc-toolbar').addEventListener('click', e => {
    const btn = e.target.closest('[data-align]');
    if (!btn) return;
    _alinear(btn.dataset.align);
  });
}

function _alinear(tipo) {
  const ir = _innerRect();
  const c  = _cert;
  switch(tipo) {
    case 'h':
      c.x = ir.x + (ir.w - c.w) / 2;
      break;
    case 'v':
      c.y = ir.y + (ir.h - c.h) / 2;
      break;
    case 'hv':
      c.x = ir.x + (ir.w - c.w) / 2;
      c.y = ir.y + (ir.h - c.h) / 2;
      break;
    case 'fit':
      _resetCert();
      break;
    case 'reset-crop':
      c.crop = { t: 0, r: 0, b: 0, l: 0 };
      break;
  }
  _render();
}

function _poblarModal() {
  if (!_ctx) return;
  const { item } = _ctx;
  _marcarActivo('enc-marcos', 'data-marco', item.frameModel ?? 'black');
  let mejorIdx = 0; // Libre por defecto
  if (item.frameRatioManual && item.frameRatio) {
    let menorDif = Infinity;
    ASPECTOS.forEach((a, i) => {
      if (!a.ratio) return;
      const dif = Math.abs(a.ratio - item.frameRatio);
      if (dif < menorDif) { menorDif = dif; mejorIdx = i; }
    });
  }
  _marcarActivo('enc-aspectos', 'data-aspecto', String(mejorIdx));
}

function _mostrarModal() {
  document.getElementById('modal-encuadrador')?.classList.add('modal--visible');
}

function _marcarActivo(contenedorId, attr, valor) {
  document.getElementById(contenedorId)
    ?.querySelectorAll('.enc-opcion')
    .forEach(b => b.classList.toggle('enc-opcion--activo', b.getAttribute(attr) === valor));
}

// ─────────────────────────────────────────────────────────────
// GUARDAR
// ─────────────────────────────────────────────────────────────

function _guardar() {
  if (!_ctx) return;
  const { item, onGuardar } = _ctx;
  const cw = _canvasW(), ch = _canvasH();

  const itemActualizado = {
    ...item,
    encuadrado: true,
    frameModel: item.frameModel ?? 'black',
    frameRatio: cw / ch,
    frameRatioManual: item.frameRatioManual ?? false,
    certState: {
      xRel: _cert.x / cw,  yRel: _cert.y / ch,
      wRel: _cert.w / cw,  hRel: _cert.h / ch,
      crop: { ..._cert.crop },
    },
  };
  cerrarEncuadrador();
  onGuardar?.(itemActualizado);
}
